// 키보드·게임패드·터치 입력을 시뮬레이션 틱 단위 InputFrame으로 모은다.
// 눌림(pressed)은 다음 sample()까지 래치되어 프레임 사이 짧은 탭도 놓치지 않는다.
import { keySlot, slotBtn, slotsAll, type SlotsView } from '../shared/skillSlots';
import { Btn, type BtnBit, type InputFrame, type InputSink } from '../shared/types';
import { keymap } from '../shared/keymap';

const KEY_BUTTON: Record<string, BtnBit> = {
  KeyJ: Btn.Attack,
  KeyK: Btn.Dodge,
  ShiftLeft: Btn.Dodge,
  ShiftRight: Btn.Dodge,
  KeyU: Btn.Skill1,
  KeyI: Btn.Skill2,
  KeyO: Btn.Skill3,
  KeyL: Btn.Skill4,
  KeyP: Btn.Skill5,
  // 물약 퀵슬롯(D2c): R = HP, T = MP
  KeyR: Btn.Potion1,
  KeyT: Btn.Potion2,
  Space: Btn.Awaken,
  KeyE: Btn.Interact,
  Enter: Btn.Interact,
  Escape: Btn.Pause,
};

/**
 * 슬롯 키(D1e, docs/SKILL-TREE.md 11.3): 숫자 1~6(윗줄·숫자패드) = 현재 페이지의 슬롯 1~6, Alt+숫자 = 다른 페이지의 슬롯, Tab = 페이지 전환.
 * 슬롯 → 스킬 번호는 슬롯 뷰(GameView.slots)가 정하고, 여기서 해당 스킬의 Btn.Skill* 비트로 바꿔 보낸다. 옛 키(U·I·O·L·P)는 그대로 있다.
 */
const DIGIT_SLOT: Readonly<Record<string, number>> = {
  Digit1: 0, Digit2: 1, Digit3: 2, Digit4: 3, Digit5: 4, Digit6: 5,
  // 7: 환생 추가 칸(레인 R). 열리지 않았으면 그 칸이 비어 있어 입력이 없다
  Digit7: 6,
  Numpad1: 0, Numpad2: 1, Numpad3: 2, Numpad4: 3, Numpad5: 4, Numpad6: 5,
};

/** 슬롯 키 연결: 슬롯 뷰 + 페이지 전환 + Tab을 가로챌지 판정(던전·일시정지 아님. 그 밖에서는 Tab이 포커스 이동에 쓰인다) */
export interface SlotKeyBinding {
  readonly view: SlotsView;
  togglePage(): void;
  tabActive(): boolean;
}

const DIR_UP = 1, DIR_DOWN = 2, DIR_LEFT = 4, DIR_RIGHT = 8;
const KEY_DIR: Record<string, number> = {
  KeyW: DIR_UP, ArrowUp: DIR_UP,
  KeyS: DIR_DOWN, ArrowDown: DIR_DOWN,
  KeyA: DIR_LEFT, ArrowLeft: DIR_LEFT,
  KeyD: DIR_RIGHT, ArrowRight: DIR_RIGHT,
};

/** 포커스된 버튼이 기본 동작(클릭·스크롤)을 해야 하는 키 */
const CONTROL_KEYS = new Set(['Enter', 'NumpadEnter', 'Space', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight']);

/**
 * 표준 게임패드 버튼 번호 → 버튼 비트. A는 공격이면서 상호작용(타이틀 시작)이다.
 * B 회피, X/Y/LB/RB 스킬, LT 천공 올려베기, RT 각성, Start 일시정지
 */
const PAD_INDEX = [0, 1, 2, 3, 4, 5, 6, 7, 9] as const;
const PAD_BITS = [Btn.Attack | Btn.Interact, Btn.Dodge, Btn.Skill1, Btn.Skill2, Btn.Skill3, Btn.Skill4, Btn.Skill5, Btn.Awaken, Btn.Pause] as const;
const PAD_DEADZONE = 0.2;

/** 키 이벤트 대상의 종류: 'text'(입력란: 게임 입력 아님), 'button'(기본 동작 보존), 'none' */
function focusKind(target: EventTarget | null): 'text' | 'button' | 'none' {
  const el = target as (HTMLElement & { isContentEditable?: boolean }) | null;
  if (!el || typeof el.tagName !== 'string') return 'none';
  // 숨겨진(display:none) 컨트롤에 남은 포커스는 무시한다.
  if (typeof el.getClientRects === 'function' && el.getClientRects().length === 0) return 'none';
  const tag = el.tagName.toUpperCase();
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable === true) return 'text';
  if (tag === 'BUTTON') return 'button';
  return 'none';
}

export class InputCollector implements InputSink {
  private readonly keys = new Set<string>();
  private dirs = 0;
  private keyHeld = 0;
  private touchHeld = 0;
  private padHeld = 0;
  private latch = 0;
  /** 눌려 있는 슬롯 키 → 눌린 순간 풀린 스킬 비트(페이지가 바뀌어도 그 키가 낸 스킬의 held가 유지된다) */
  private readonly slotHeld = new Map<string, number>();
  private slotKeys: SlotKeyBinding | null = null;
  private stickX = 0;
  private stickZ = 0;
  private padX = 0;
  private padZ = 0;
  /** 자동 퀘스트 이동(화면 축). 직접 이동 입력이 없을 때만 쓴다 */
  private autoX = 0;
  private autoZ = 0;
  /** pollFrame()을 한 번이라도 부르면 패드 폴링은 렌더 프레임 쪽이 맡는다 */
  private framePolling = false;
  private readonly frame: InputFrame = { moveX: 0, moveZ: 0, held: 0, pressed: 0 };

  /** 슬롯 키(1~6·Alt+1~6·Tab)를 켠다. 슬롯 뷰를 읽기만 한다. null이면 끈다 */
  setSlotKeys(binding: SlotKeyBinding | null): void {
    this.slotKeys = binding;
    if (!binding) { this.slotHeld.clear(); this.updateKeys(); }
  }

  /** 슬롯 키 처리. 처리했으면 true(일반 키 경로를 건너뛴다) */
  private slotKeyDown(e: KeyboardEvent, kind: 'text' | 'button' | 'none'): boolean {
    const sk = this.slotKeys;
    if (!sk) return false;
    if (e.code === 'Tab') {
      if (kind === 'text' || e.ctrlKey || e.metaKey || e.altKey || e.shiftKey || !sk.tabActive()) return false;
      if (!e.repeat) sk.togglePage();
      e.preventDefault();
      return true;
    }
    const n = DIGIT_SLOT[e.code];
    if (n === undefined || e.ctrlKey || e.metaKey) return false;
    if (kind === 'text') return false;
    if (e.altKey) e.preventDefault(); // Alt+숫자의 브라우저 기본 동작(탭 전환 등)을 막는다. 숫자만은 기본 동작이 없다
    if (e.repeat) return true;
    const bit = slotBtn(slotsAll(sk.view), keySlot(sk.view.page, n, e.altKey));
    if (bit === 0) return true; // 빈 칸·범위 밖: 입력 없음
    if (((this.keyHeld | this.touchHeld | this.padHeld) & bit) === 0) this.latch |= bit;
    this.slotHeld.set(e.code, bit);
    this.keys.add(e.code);
    this.updateKeys();
    return true;
  }

  attach(target: Window): () => void {
    const down = (e: KeyboardEvent) => {
      if (DIGIT_SLOT[e.code] !== undefined || e.code === 'Tab') {
        if (this.slotKeys && this.slotKeyDown(e, focusKind(e.target))) return;
      }
      const d = KEY_DIR[e.code];
      // PC RPG식 배치(shared/keymap.ts)에서는 K가 회피가 아니라 스킬 창 단축키다(ui/hotkeys.ts가 처리)
      const b = e.code === 'KeyK' && keymap.preset !== 'action' ? undefined : KEY_BUTTON[e.code];
      if (!d && !b) return;
      const kind = focusKind(e.target);
      if (kind === 'text') return; // 입력란에 타이핑 중인 키는 게임으로 보내지 않는다.
      if (b && !e.repeat && ((this.keyHeld | this.touchHeld | this.padHeld) & b) === 0) this.latch |= b;
      this.keys.add(e.code);
      this.updateKeys();
      // 화면에 보이는 버튼에 포커스가 있으면 Enter/Space/방향키의 기본 동작을 막지 않는다.
      if (!(kind === 'button' && CONTROL_KEYS.has(e.code))) e.preventDefault();
    };
    const up = (e: KeyboardEvent) => {
      if (!this.keys.delete(e.code)) return;
      this.slotHeld.delete(e.code);
      this.updateKeys();
    };
    const blur = () => {
      this.dirs = 0;
      this.keys.clear();
      this.slotHeld.clear();
      this.keyHeld = 0;
      this.touchHeld = 0;
      this.stickX = 0;
      this.stickZ = 0;
      this.latch = 0;
      this.padHeld = 0;
      this.padX = this.padZ = 0;
      this.autoX = this.autoZ = 0;
    };
    target.addEventListener('keydown', down);
    target.addEventListener('keyup', up);
    target.addEventListener('blur', blur);
    return () => {
      target.removeEventListener('keydown', down);
      target.removeEventListener('keyup', up);
      target.removeEventListener('blur', blur);
      blur();
    };
  }

  private updateKeys(): void {
    this.dirs = 0; this.keyHeld = 0;
    // W/위 화살표, K/Shift 중 하나만 떼도 다른 키의 held는 유지한다.
    for (const code of this.keys) {
      this.dirs |= KEY_DIR[code] ?? 0;
      this.keyHeld |= KEY_BUTTON[code] ?? this.slotHeld.get(code) ?? 0;
    }
  }

  // InputSink(터치 UI)
  setStick(x: number, z: number): void {
    this.stickX = Number.isFinite(x) ? x : 0;
    this.stickZ = Number.isFinite(z) ? z : 0;
  }
  buttonDown(btn: BtnBit): void {
    if (((this.touchHeld | this.keyHeld | this.padHeld) & btn) === 0) this.latch |= btn;
    this.touchHeld |= btn;
  }
  buttonUp(btn: BtnBit): void {
    this.touchHeld &= ~btn;
  }
  setAuto(x: number, z: number): void {
    this.autoX = Number.isFinite(x) ? x : 0;
    this.autoZ = Number.isFinite(z) ? z : 0;
  }
  manualMove(): boolean {
    return this.dirs !== 0 || this.stickX !== 0 || this.stickZ !== 0 || this.padX !== 0 || this.padZ !== 0;
  }

  /**
   * 렌더 프레임마다 1회 호출: 게임패드 상태를 읽는다(getGamepads는 틱마다 부르기에 비싸다).
   * 한 번이라도 호출되면 sample()은 더 이상 패드를 직접 읽지 않는다.
   */
  pollFrame(): void {
    this.framePolling = true;
    this.pollPad();
  }

  private pollPad(): void {
    const nav = typeof navigator !== 'undefined' ? navigator : null;
    const pads = nav && typeof nav.getGamepads === 'function' ? nav.getGamepads() : null;
    let held = 0;
    let px = 0, pz = 0;
    if (pads) {
      for (let n = 0; n < pads.length; n++) {
        const p = pads[n];
        if (!p || !p.connected) continue;
        for (let k = 0; k < PAD_INDEX.length; k++) if (p.buttons[PAD_INDEX[k]]?.pressed) held |= PAD_BITS[k];
        const ax = p.axes[0] ?? 0, az = p.axes[1] ?? 0;
        if (Math.abs(ax) > PAD_DEADZONE) px = ax;
        if (Math.abs(az) > PAD_DEADZONE) pz = az;
        break;
      }
    }
    this.latch |= held & ~this.padHeld;
    this.padHeld = held;
    this.padX = px;
    this.padZ = pz;
  }

  /** 시뮬레이션 1틱마다 호출. 같은 객체를 재사용한다. */
  sample(): InputFrame {
    if (!this.framePolling) this.pollPad();
    let x = 0, z = 0;
    if (this.dirs & DIR_LEFT) x -= 1;
    if (this.dirs & DIR_RIGHT) x += 1;
    if (this.dirs & DIR_UP) z -= 1;
    if (this.dirs & DIR_DOWN) z += 1;
    if (x === 0 && z === 0) {
      x = this.stickX;
      z = this.stickZ;
    }
    if (x === 0 && z === 0) {
      x = this.padX;
      z = this.padZ;
    }
    if (x === 0 && z === 0) {
      x = this.autoX;
      z = this.autoZ;
    }
    const len = Math.hypot(x, z);
    if (len > 1) {
      x /= len;
      z /= len;
    }
    const f = this.frame;
    f.moveX = x;
    f.moveZ = z;
    f.held = this.keyHeld | this.touchHeld | this.padHeld;
    f.pressed = this.latch;
    this.latch = 0;
    return f;
  }
}
